import { Controller } from "stimulus"

// Turbo gives a lazy frame exactly one chance: `loadSourceURL` calls
// `appearanceObserver.stop()` right after starting the visit, before knowing whether it
// succeeded. A frame whose fetch failed therefore stays on its placeholder until a full
// page reload. This controller keeps retrying while the frame is on screen, backs off
// between attempts, and stops as soon as it loads or leaves the screen.
//
// Attach to the <turbo-frame> itself: Turbo replaces the frame's children, not the frame,
// so the controller survives every load.
const INITIAL_DELAY = 1000
const MAX_DELAY = 15000

export default class extends Controller {
  initialize() {
    this.onError = this.scheduleRetry.bind(this)
    this.onLoad = this.stop.bind(this)
    this.onOnline = this.retryNow.bind(this)
    this.onClick = this.handleShowClick.bind(this)
  }

  connect() {
    this.attempt = 0
    this.element.addEventListener("turbo:fetch-request-error", this.onError)
    this.element.addEventListener("turbo:frame-load", this.onLoad)
    window.addEventListener("online", this.onOnline)
    document.addEventListener("click", this.onClick)
  }

  disconnect() {
    this.clearTimer()
    this.element.removeEventListener("turbo:fetch-request-error", this.onError)
    this.element.removeEventListener("turbo:frame-load", this.onLoad)
    window.removeEventListener("online", this.onOnline)
    document.removeEventListener("click", this.onClick)
  }

  // Reopening the modal has to retry by hand: Turbo's appearance observer is already
  // stopped, so becoming visible again triggers nothing. Deferred to the next tick so the
  // modal is visible by the time we check.
  handleShowClick(event) {
    if (!event.target.closest(`.js-modal-action-show[data-modal-id="${this.element.id}"]`)) return

    // Same timer slot as the backoff, so `disconnect` clears it and reopening the modal
    // cancels a retry that was already queued.
    this.clearTimer()
    this.timer = setTimeout(() => this.retryNow(), 0)
  }

  retryNow() {
    this.attempt = 0
    this.reload()
  }

  scheduleRetry() {
    this.clearTimer()
    const delay = Math.min(INITIAL_DELAY * 2 ** this.attempt, MAX_DELAY)
    this.attempt += 1
    this.timer = setTimeout(() => this.reload(), delay)
  }

  // A failed reload fires turbo:fetch-request-error again, which schedules the next one.
  reload() {
    if (this.loaded || !this.visible) return this.stop()

    this.element.reload()
  }

  stop() {
    this.clearTimer()
    this.attempt = 0
  }

  clearTimer() {
    if (!this.timer) return

    clearTimeout(this.timer)
    this.timer = null
  }

  get loaded() {
    return this.element.hasAttribute("complete")
  }

  // getClientRects is empty for a display:none element, and works on old WebViews where
  // checkVisibility is unavailable.
  get visible() {
    return this.element.getClientRects().length > 0
  }
}
